import React from "react";
import {
  LayoutDashboard, MessageCircleHeart, Rss, Dumbbell, ClipboardList, Apple,
  Scale, HeartPulse, CalendarHeart, Watch, Globe2, Trophy, Flag, Award, Crown,
  Users, UsersRound, Bell, CreditCard, ShieldPlus, User, Plus, Home, Gift,
  Compass, LogOut, Sparkles, ShieldCheck, Menu, X, HeartHandshake, Baby,
} from "lucide-react";
import Logo from "../Logo";
import { useAuth } from "../../lib/auth";
import { isNativeApp } from "../../lib/platform";
import api from "../../lib/api";
import { toast } from "sonner";

function buildGroups(member) {
  const isFemale = member?.gender !== "male";
  const cycleLabel = member?.gender === "male" ? "Daily Edge" : "Cycle Tracker";
  const moveItems = [
      { id: "workouts", label: "Workouts", icon: Dumbbell },
      { id: "workout", label: "Workout Logger", icon: ClipboardList },
      { id: "nutrition", label: "Nutrition", icon: Apple },
      { id: "weight", label: "Weight", icon: Scale },
      { id: "wellness", label: "Wellness", icon: HeartPulse },
      { id: "cycle", label: cycleLabel, icon: CalendarHeart },
  ];
  if (isFemale) moveItems.push({ id: "pregnancy", label: "Pregnancy", icon: Baby });
  moveItems.push({ id: "wearables", label: "Wearables", icon: Watch });
  const groups = [
    { title: "Home", items: [
      { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
      { id: "coaches", label: "AI Coaches", icon: MessageCircleHeart },
      { id: "feed", label: "Activity Feed", icon: Rss },
    ]},
    { title: "Move", items: moveItems },
    { title: "World", items: [
      { id: "passport", label: "Travel Passport", icon: Globe2 },
      { id: "leaderboard", label: "Leaderboard", icon: Trophy },
      { id: "challenges", label: "Challenges", icon: Flag },
      { id: "halloffame", label: "Hall of Fame", icon: Crown },
      { id: "badges", label: "Achievements", icon: Award },
    ]},
    { title: "Circle", items: [
      { id: "community", label: "Community", icon: Users },
      { id: "partner", label: "Partner", icon: HeartHandshake },
      { id: "family", label: "Family Plan", icon: UsersRound },
      { id: "referrals", label: "Invite Friends", icon: Gift },
      { id: "notifications", label: "Notifications", icon: Bell },
    ]},
    { title: "Account", items: [
      { id: "subscription", label: "Subscription", icon: CreditCard },
      { id: "medical", label: "Medical", icon: ShieldPlus },
      { id: "profile", label: "Profile", icon: User },
    ]},
  ];
  if (member?.role === "admin") {
    groups[groups.length - 1].items.push({ id: "admin", label: "Admin", icon: ShieldCheck });
  }
  return groups;
}

export function Sidebar({ currentPage, onNavigate, unread = 0 }) {
  const { member, logout } = useAuth();
  const groups = buildGroups(member);
  return (
    <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col border-r border-white/[0.06] bg-ink-surface/60 lg:flex">
      <div className="px-6 py-6">
        <button onClick={() => onNavigate("dashboard")}><Logo size={28} /></button>
      </div>
      <nav className="flex-1 space-y-5 overflow-y-auto px-4 pb-4">
        {groups.map((g) => (
          <div key={g.title}>
            <div className="mb-1.5 px-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-mist-muted/60">{g.title}</div>
            <div className="space-y-0.5">
              {g.items.map((n) => {
                const active = currentPage === n.id;
                return (
                  <button key={n.id} onClick={() => onNavigate(n.id)}
                    className={`flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-all ${active ? "bg-brand/15 text-mist" : "text-mist-muted hover:bg-white/[0.04] hover:text-mist"}`}>
                    <n.icon size={17} className={active ? "text-brand" : ""} /> <span className="flex-1 text-left">{n.label}</span>
                    {n.id === "notifications" && unread > 0 && <NavBadge count={unread} />}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </nav>
      <div className="border-t border-white/[0.06] px-4 py-4">
        {member?.subscriptionStatus === "trial" && (
          <div className="mb-3 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-3">
            <div className="flex items-center gap-1.5 text-xs font-medium text-brand-soft"><Sparkles size={12} /> Trial · {member?.trialDaysLeft}d left</div>
            {!isNativeApp() && <button onClick={() => onNavigate("subscription")} className="btn-primary mt-2 w-full !py-1.5 text-xs">Upgrade</button>}
          </div>
        )}
        <button onClick={() => { logout(); onNavigate("login"); }} className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium text-mist-muted transition-colors hover:text-mist"><LogOut size={17} /> Sign out</button>
      </div>
    </aside>
  );
}

export function TopBar({ onNavigate, onOpenMenu, unread = 0 }) {
  return (
    <div className="sticky top-0 z-30 flex items-center justify-between border-b border-white/[0.06] bg-ink/80 px-4 py-3 backdrop-blur-xl lg:hidden">
      <div className="flex items-center gap-1">
        <button onClick={onOpenMenu} data-testid="mobile-menu-btn" className="-ml-1 p-1 text-mist" aria-label="Open menu"><Menu size={24} /></button>
        <button onClick={() => onNavigate("dashboard")}><Logo size={24} /></button>
      </div>
      <button onClick={() => onNavigate("notifications")} data-testid="topbar-bell" className="relative text-mist-muted" aria-label="Notifications">
        <Bell size={20} />
        {unread > 0 && <span data-testid="topbar-bell-badge" className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-brand px-1 text-[9px] font-bold text-[#05101f]">{unread > 9 ? "9+" : unread}</span>}
      </button>
    </div>
  );
}

function NavBadge({ count }) {
  return (
    <span data-testid="nav-unread-badge" className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-brand px-1.5 text-[10px] font-bold text-[#05101f]">
      {count > 9 ? "9+" : count}
    </span>
  );
}

export function MobileMenu({ open, currentPage, onClose, onNavigate, unread = 0 }) {
  const { member, logout } = useAuth();
  if (!open) return null;
  const groups = buildGroups(member);
  const go = (id) => { onNavigate(id); onClose(); };
  return (
    <div className="fixed inset-0 z-[60] lg:hidden" data-testid="mobile-menu">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in" onClick={onClose} />
      <div className="absolute inset-y-0 left-0 flex w-[84%] max-w-xs flex-col bg-ink-surface shadow-2xl">
        <div className="flex items-center justify-between border-b border-white/[0.06] px-5 py-4">
          <Logo size={26} />
          <button onClick={onClose} data-testid="mobile-menu-close" className="text-mist-muted" aria-label="Close menu"><X size={22} /></button>
        </div>
        <nav className="flex-1 space-y-5 overflow-y-auto px-4 py-4">
          {groups.map((g) => (
            <div key={g.title}>
              <div className="mb-1.5 px-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-mist-muted/60">{g.title}</div>
              <div className="space-y-0.5">
                {g.items.map((n) => {
                  const active = currentPage === n.id;
                  return (
                    <button key={n.id} onClick={() => go(n.id)} data-testid={`mobile-nav-${n.id}`}
                      className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${active ? "bg-brand/15 text-mist" : "text-mist-muted hover:bg-white/[0.04] hover:text-mist"}`}>
                      <n.icon size={18} className={active ? "text-brand" : ""} /> <span className="flex-1 text-left">{n.label}</span>
                      {n.id === "notifications" && unread > 0 && <NavBadge count={unread} />}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
        <div className="border-t border-white/[0.06] px-4 py-4">
          {member?.subscriptionStatus === "trial" && (
            <div className="mb-3 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-3">
              <div className="flex items-center gap-1.5 text-xs font-medium text-brand-soft"><Sparkles size={12} /> Trial · {member?.trialDaysLeft}d left</div>
              {!isNativeApp() && <button onClick={() => go("subscription")} className="btn-primary mt-2 w-full !py-1.5 text-xs">Upgrade</button>}
            </div>
          )}
          <button onClick={() => { logout(); onClose(); onNavigate("login"); }} data-testid="mobile-signout" className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium text-mist-muted transition-colors hover:text-mist"><LogOut size={17} /> Sign out</button>
        </div>
      </div>
    </div>
  );
}

const tabs = [
  { id: "dashboard", label: "Home", icon: Home },
  { id: "workouts", label: "Train", icon: Dumbbell },
  { id: "checkin", label: "Check In", icon: Plus, center: true },
  { id: "feed", label: "Feed", icon: Compass },
  { id: "profile", label: "You", icon: User },
];

export function BottomTabBar({ currentPage, onNavigate, onQuickAdd }) {
  return (
    <nav className="fixed inset-x-0 bottom-0 z-40 flex items-end justify-around border-t border-white/[0.06] bg-ink/90 px-2 pb-2 pt-1.5 backdrop-blur-xl lg:hidden">
      {tabs.map((t) => {
        if (t.center) {
          return (
            <button key={t.id} onClick={onQuickAdd} data-testid="quick-add-btn" className="btn-primary -mt-6 flex h-14 w-14 items-center justify-center !rounded-full !p-0 shadow-lg" aria-label="Quick add"><t.icon size={24} /></button>
          );
        }
        const active = currentPage === t.id;
        return (
          <button key={t.id} onClick={() => onNavigate(t.id)} className={`flex flex-1 flex-col items-center gap-1 py-1.5 text-[10px] font-medium ${active ? "text-brand" : "text-mist-muted"}`}>
            <t.icon size={20} /> {t.label}
          </button>
        );
      })}
    </nav>
  );
}

export function DesktopQuickAddButton({ onClick }) {
  const { member } = useAuth();
  const [showTip, setShowTip] = React.useState(false);
  React.useEffect(() => {
    const done = (member?.dismissedNudges || []).includes("quickadd_tip") || localStorage.getItem("wyvn_quickadd_tip");
    if (!done) {
      const t = setTimeout(() => setShowTip(true), 1500);
      return () => clearTimeout(t);
    }
  }, [member]);
  const dismissTip = () => { localStorage.setItem("wyvn_quickadd_tip", "1"); setShowTip(false); api.dismissNudge("quickadd_tip").catch(() => {}); };

  return (
    <div className="fixed bottom-8 right-8 z-40 hidden lg:block">
      {showTip && (
        <div data-testid="quick-add-tip" className="absolute bottom-1/2 right-16 w-56 translate-y-1/2 rounded-2xl border border-white/10 bg-ink-surface p-3.5 shadow-2xl" style={{ animation: "aeva-rise 0.3s cubic-bezier(0.22,1,0.36,1) both" }}>
          <div className="flex items-start gap-2">
            <Sparkles size={16} className="mt-0.5 shrink-0 text-brand" />
            <p className="text-xs leading-relaxed text-mist">Tip: press <kbd className="rounded bg-white/10 px-1.5 py-0.5 font-mono text-[11px] text-mist">N</kbd> anywhere to quickly log anything.</p>
          </div>
          <button onClick={dismissTip} data-testid="quick-add-tip-dismiss" className="mt-2 w-full rounded-lg bg-brand/15 py-1.5 text-xs font-medium text-brand transition-colors hover:bg-brand/25">Got it</button>
          <span className="absolute -right-1.5 top-1/2 h-3 w-3 -translate-y-1/2 rotate-45 border-b border-r border-white/10 bg-ink-surface" />
        </div>
      )}
      <button onClick={() => { dismissTip(); onClick(); }} data-testid="desktop-quick-add-btn" aria-label="Quick add"
        className="btn-primary flex h-14 w-14 items-center justify-center !rounded-full !p-0 shadow-xl transition-transform hover:scale-105">
        <Plus size={26} />
      </button>
    </div>
  );
}

export function QuickAddSheet({ open, onClose, onNavigate, onQuickLog }) {
  const { updateMember } = useAuth();
  if (!open) return null;

  const doCheckIn = async () => {
    onClose();
    try {
      const res = await api.checkin();
      updateMember({ points: res.points, streak: res.streak });
      toast.success(res.already ? "Already checked in today" : "Checked in! 🔥", { description: res.already ? "See you tomorrow" : "+25 points · streak extended" });
      onNavigate("dashboard");
    } catch (e) { toast.error("Check-in failed", { description: e.message }); }
  };
  const log = (mode) => { onClose(); onQuickLog(mode); };

  const actions = [
    { id: "checkin", label: "Daily check-in", desc: "+25 points · keep your streak", icon: Sparkles, onClick: doCheckIn },
    { id: "workout", label: "Log a workout", desc: "Sets, reps & weight", icon: Dumbbell, onClick: () => log("workout") },
    { id: "nutrition", label: "Log nutrition", desc: "Meals, water & macros", icon: Apple, onClick: () => log("nutrition") },
    { id: "weight", label: "Log weight", desc: "Track your trend", icon: Scale, onClick: () => log("weight") },
  ];

  return (
    <div className="fixed inset-0 z-[70]" data-testid="quick-add-sheet">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in" onClick={onClose} />
      {/* mobile: bottom sheet · desktop: centered dialog */}
      <div className="absolute inset-x-0 bottom-0 rounded-t-3xl border-t border-white/10 bg-ink-surface p-4 pb-8 lg:inset-auto lg:bottom-24 lg:right-8 lg:w-96 lg:rounded-3xl lg:border lg:pb-4 lg:shadow-2xl"
        style={{ animation: "aeva-rise 0.28s cubic-bezier(0.22,1,0.36,1) both" }}>
        <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-white/15 lg:hidden" />
        <div className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-mist-muted/70">Quick add</div>
        <div className="space-y-1.5">
          {actions.map((a) => (
            <button key={a.id} onClick={a.onClick} data-testid={`quick-action-${a.id}`} className="flex w-full items-center gap-3 rounded-2xl border border-white/[0.06] bg-white/[0.02] px-4 py-3 text-left transition-colors hover:bg-white/[0.05]">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand/15 text-brand"><a.icon size={18} /></span>
              <span className="flex-1"><span className="block text-sm font-semibold text-mist">{a.label}</span><span className="block text-xs text-mist-muted">{a.desc}</span></span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
